import { useMemo, useState } from 'react';
import { Badge, Button, MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle, makeStyles } from '@fluentui/react-components';
import { Check, Plus, RefreshCw, Undo2 } from 'lucide-react';

import { extractConsentError, isGraphError } from '../../api/identity';
import { IdentitySuggestionUser, SuggestionCandidate } from '../../types/identity';
import { IdentityWorkbench } from '../../hooks/useIdentityWorkbench';
import { DataTable, DataTableColumn } from '../DataTable';
import ConsentMessageBar from './ConsentMessageBar';
import IdentityBulkReviewPanel from './IdentityBulkReviewPanel';
import MappingStatusChip, { deriveMappingChipStatus } from './MappingStatusChip';
import {
  DestinationView,
  FILTER_LABELS,
  MATCHED_ON_LABELS,
  RowVM,
  deriveRowActions,
  errorMessage,
  resolveDestination,
  sourceIdentity,
  truncateSid,
} from './identityRowModel';

const useStyles = makeStyles({
  serialList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
    marginTop: '4px',
  },
  // FD-382 — the alternate-candidate chooser renders INLINE rather than in a
  // Fluent Menu. Portal-mounted Fluent surfaces are the repo's known jsdom
  // hazard (FD-372 restructured a whole suite around a Dropdown popup that
  // closes on blur under load, and SourceGroupPicker's suite deliberately never
  // opens one). An inline disclosure needs no portal, is directly assertable,
  // and shows every candidate with its match tier side by side instead of
  // hiding them behind a menu the operator has to know to open.
  altList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '4px',
    marginTop: '6px',
  },
  altRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '6px',
    flexWrap: 'wrap',
  },
  tableSurface: {
    backgroundColor: '#ffffff',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    overflowX: 'auto',
  },
  saveBar: {
    position: 'sticky',
    bottom: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    columnGap: '8px',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '16px',
    paddingRight: '16px',
    backgroundColor: '#ffffff',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: '#e4e4e7',
    boxShadow: '0 -4px 12px -8px rgba(24, 24, 27, 0.15)',
    zIndex: 1,
  },
  saveBarCount: {
    marginRight: 'auto',
    fontSize: '13px',
    color: '#3f3f46',
  },
  stateBlock: {
    paddingTop: '24px',
    paddingBottom: '24px',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  loadMoreRow: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '8px',
    paddingBottom: '4px',
  },
  cellStack: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
  },
  destCell: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    flexWrap: 'wrap',
    rowGap: '4px',
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '4px',
  },
});

type QueueStyles = ReturnType<typeof useStyles>;

export interface IdentityQueueTableProps {
  wb: IdentityWorkbench;
  deviceCount: number;
  onAssignDevices?: () => void;
}

/**
 * The queue itself — the table, its error and empty states, the "Load more
 * users" control and the sticky staged-save bar, which belongs with the table
 * it commits rather than beside it.
 */
export default function IdentityQueueTable({
  wb,
  deviceCount,
  onAssignDevices,
}: IdentityQueueTableProps) {
  const styles = useStyles();
  // FD-510 — the review step, collapsed by default. Opening it is how an
  // operator sees WHAT is about to be written before pressing Save.
  const [reviewOpen, setReviewOpen] = useState(false);
  const {
    query,
    mappingsQuery,
    viewRows,
    staged,
    stageEdit,
    unstage,
    frozen,
    isT2T,
    filter,
    setFilter,
    devicesWithoutManifest,
    expandedDevices,
    toggleDevices,
    expandedAlternates,
    toggleAlternates,
    handleApprove,
    handleRemove,
    selectedKeys,
    setSelectedKeys,
    setSelectedRowKey,
    isSaving,
    handleSave,
    discardStaged,
  } = wb;

  const columns = useMemo<DataTableColumn<RowVM>[]>(
    () => [
      {
        // The source user, named the way an operator names people: display name
        // over UPN (2026-09-27 owner decision — the SID was the second line and
        // is not a thing anyone reads a table by).
        //
        // The SID is the JOIN KEY and, on D2C, it IS the identity (FD-291), so
        // it is not deleted — it falls back in when there is no UPN to show, and
        // the drawer carries it in full either way. A row that showed neither
        // would be unidentifiable.
        key: 'sourceUser',
        header: 'Source user',
        render: (vm) => {
          const { name, upn, sid } = sourceIdentity(vm);
          const primary = name ?? upn ?? (sid ? truncateSid(sid) : '—');
          // Only ever one identifier under the name: the UPN when there is one,
          // otherwise the SID. Both would be the pre-2026-09-27 cell with an
          // extra line.
          const secondary = name ? (upn ?? (sid ? truncateSid(sid) : null)) : null;
          return (
            <div className={styles.cellStack}>
              <span className="font-medium text-zinc-900" title={sid ?? undefined}>
                {primary}
              </span>
              {secondary && (
                <span className="text-xs text-zinc-500" title={sid ?? undefined}>
                  {secondary}
                </span>
              )}
            </div>
          );
        },
      },
      {
        // FD-495's destination column split in two (2026-09-27 owner decision):
        // WHO on the destination side sits next to who on the source side, so
        // the pair reads across, and the tier that matched them is its own
        // column. Both are resolved by `resolveDestination` so they cannot
        // disagree.
        key: 'destinationUser',
        header: 'Destination user',
        render: (vm) =>
          renderDestinationUserCell(resolveDestination(vm, staged), styles, {
            expanded: vm.kind === 'suggestion' && expandedAlternates.has(vm.key),
            onToggleAlternates: () => toggleAlternates(vm.key),
            onUseCandidate: handleApprove,
          }),
      },
      {
        key: 'matchType',
        header: 'Match type',
        render: (vm) => renderMatchTypeCell(resolveDestination(vm, staged)),
      },
      {
        key: 'devices',
        header: 'Devices',
        numeric: true,
        align: 'right',
        // FD-384 — the serials were already on the row but reachable only as a
        // `title` tooltip, which is invisible to touch, to keyboard, and to
        // anyone who does not happen to hover. The blast radius of a mapping is
        // exactly the thing to see before approving it.
        render: (vm) => {
          // A mapping-only row has no device by construction — that is exactly
          // why the suggestions feed had nothing to hang it on.
          if (vm.kind !== 'suggestion') return <span className="text-zinc-400">—</span>;
          const { row } = vm;
          if (row.deviceCount === 0) return <span className="text-zinc-400">0</span>;
          const open = expandedDevices.has(vm.key);
          return (
            // The row opens the drawer, so every control inside it stops
            // propagation — otherwise expanding the serial list would also open
            // the drawer over it.
            <div onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                appearance="transparent"
                aria-expanded={open}
                aria-label={`${row.deviceCount} device${
                  row.deviceCount === 1 ? '' : 's'
                } for this identity`}
                onClick={() => toggleDevices(vm.key)}
              >
                {row.deviceCount}
              </Button>
              {open && (
                <div className={styles.serialList}>
                  {row.serialNumbers.map((sn) => (
                    <span key={sn} className="font-mono text-[11px] text-zinc-600">
                      {sn}
                    </span>
                  ))}
                  {onAssignDevices && (
                    <Button size="small" appearance="subtle" onClick={onAssignDevices}>
                      View devices
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        render: (vm) => {
          if (vm.kind === 'manual') return <MappingStatusChip status="pending" />;
          const edit = staged.get(vm.key);
          if (edit) return <MappingStatusChip status="pending" />;
          if (vm.kind === 'mapping') return <MappingStatusChip status="mapped" />;
          return <MappingStatusChip status={deriveMappingChipStatus(vm.row)} />;
        },
      },
      {
        key: 'actions',
        header: '',
        // FD-509 — WHAT is offered comes from `deriveRowActions`, shared with
        // the inspector. This cell decides only how to draw it. FD-495 refused
        // to give the drawer actions because a second set of buttons would be a
        // second implementation of the FD-ADR-036 freeze presentation and the
        // two would drift; one derivation is what answers that rather than
        // overruling it.
        render: (vm) => {
          const actions = deriveRowActions(vm, staged, { frozen, isT2T });

          if (actions.kind === 'orphanMapping') {
            if (!actions.removable) {
              return <div className={styles.rowActions} onClick={(e) => e.stopPropagation()} />;
            }
            return (
              <div className={styles.rowActions} onClick={(e) => e.stopPropagation()}>
                <Button
                  size="small"
                  appearance="subtle"
                  onClick={() =>
                    stageEdit(vm.key, {
                      kind: 'remove',
                      label: actions.removeLabel,
                      sourceKey: actions.removeSourceKey,
                    })
                  }
                >
                  Remove
                </Button>
              </div>
            );
          }

          if (actions.kind === 'staged') {
            return (
              <div className={styles.rowActions} onClick={(e) => e.stopPropagation()}>
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<Undo2 size={14} strokeWidth={1.5} />}
                  onClick={() => unstage(vm.key)}
                >
                  Undo
                </Button>
              </div>
            );
          }

          if (actions.kind !== 'suggestion') return null;

          return (
            <div className={styles.rowActions} onClick={(e) => e.stopPropagation()}>
              {actions.approvable && actions.candidate && (
                <Button
                  size="small"
                  appearance="primary"
                  icon={<Check size={14} strokeWidth={1.5} />}
                  onClick={() => handleApprove(actions.row, actions.candidate!)}
                >
                  Approve
                </Button>
              )}
              {/* FD-546 — THERE IS NO "Change" BUTTON, and its absence is the
                  decision (owner, 2026-10-01). Changing a destination happens in
                  the inspector, which the row click opens and which has carried
                  its own destination search since FD-517. The button opened the
                  pre-FD-517 modal, so the control with the obvious label was the
                  one that could NOT reach the new panel — this cell
                  `stopPropagation()`s, so clicking it was the single gesture
                  guaranteed to miss it.

                  The FD-ADR-037 §4 freeze presentation moved WITH the action
                  rather than being lost: the inspector's search affordance is
                  disabled with `changeDisabledReason`, from the same
                  `deriveRowActions` this cell reads. The cost is that an active
                  wave no longer announces the refusal on the row — the operator
                  meets it one click later, still ahead of the call, which is
                  what §4 requires. */}
              {/* Removal is a WHOLE-REQUEST 409 while the wave is active, not a
                  per-row reject, so the button is withheld rather than
                  disabled-with-a-reason: offering it would stage an edit whose
                  save fails entirely, taking every other staged change with it. */}
              {actions.removable && (
                <Button
                  size="small"
                  appearance="subtle"
                  onClick={() => handleRemove(actions.row)}
                >
                  Remove
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [staged, isT2T, styles, frozen, expandedAlternates, expandedDevices, onAssignDevices],
  );

  const consent = query.isError ? extractConsentError(query.error) : null;

  let body: JSX.Element;
  if (query.isError) {
    body = (
      <div className={styles.stateBlock}>
        {consent ? (
          <ConsentMessageBar consent={consent} />
        ) : (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {isGraphError(query.error)
                  ? 'Directory temporarily unavailable'
                  : 'Could not load identity suggestions'}
              </MessageBarTitle>
              {isGraphError(query.error)
                ? 'Microsoft Graph did not respond. This is usually transient — retry in a moment.'
                : errorMessage(query.error)}
            </MessageBarBody>
            <MessageBarActions>
              <Button
                size="small"
                appearance="subtle"
                icon={<RefreshCw size={14} strokeWidth={1.5} />}
                onClick={() => query.refetch()}
              >
                Retry
              </Button>
            </MessageBarActions>
          </MessageBar>
        )}
      </div>
    );
  } else if (!query.isLoading && viewRows.length === 0) {
    const savedCount = mappingsQuery.data?.mappings.length ?? 0;
    body = (
      <div className={styles.stateBlock}>
        {/* FD-387 — the filter is checked BEFORE the device-count story. A
            campaign with saved mappings and no devices used to be told "assign
            devices first" while its mappings sat on the server, which reads as
            "nothing was saved". Say what is actually true: rows exist, this
            view is not showing them. */}
        {savedCount > 0 && filter !== 'all' ? (
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>Nothing matches “{FILTER_LABELS[filter]}”</MessageBarTitle>
              This campaign has {savedCount} saved identity mapping
              {savedCount === 1 ? '' : 's'} — they are not shown by this filter.
            </MessageBarBody>
            <MessageBarActions>
              <Button size="small" appearance="primary" onClick={() => setFilter('all')}>
                Show all
              </Button>
            </MessageBarActions>
          </MessageBar>
        ) : deviceCount === 0 ? (
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>No devices assigned yet</MessageBarTitle>
              Identity mappings are derived from device manifests — assign devices first.
            </MessageBarBody>
            {onAssignDevices && (
              <MessageBarActions>
                <Button
                  size="small"
                  appearance="primary"
                  icon={<Plus size={14} strokeWidth={1.5} />}
                  onClick={onAssignDevices}
                >
                  Assign devices
                </Button>
              </MessageBarActions>
            )}
          </MessageBar>
        ) : (
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>Waiting for device manifests</MessageBarTitle>
              Mappings unlock as devices check in and submit manifests.
              {devicesWithoutManifest > 0 && (
                <>
                  {' '}
                  <span className="font-mono">{devicesWithoutManifest}</span> device
                  {devicesWithoutManifest === 1 ? ' has' : 's have'} not submitted a manifest
                  yet.
                </>
              )}
            </MessageBarBody>
          </MessageBar>
        )}
      </div>
    );
  } else {
    body = (
      <DataTable
        columns={columns}
        data={viewRows}
        keyForRow={(vm) => vm.key}
        rowDomId={(vm) => `mapping-row-${vm.key}`}
        isLoading={query.isLoading}
        loadingRowCount={4}
        // The ROW opens the drawer (2026-09-27 owner decision), replacing
        // FD-495's dedicated `···`-style opener at the row's right edge.
        //
        // That opener existed because this row carries Approve, Change, Remove,
        // Undo, the device expander and the alternate chooser, none of which
        // stopped propagation — a row handler would have opened the drawer on
        // every approve. Each of those now stops propagation at its own
        // container, which is the same discipline `DataTable` already applies to
        // the selection checkbox cell, so the row handler is safe to add.
        //
        // Keyboard reach is `DataTable`'s to provide, not this component's: any
        // row-click table here has the same gap, and closing it in one place is
        // the fix rather than keeping one table on a bespoke button.
        onRowClick={(vm) => setSelectedRowKey(vm.key)}
        selection={{
          selectedKeys,
          onSelectionChange: setSelectedKeys,
          keyForSelection: (vm) => vm.key,
        }}
      />
    );
  }

  return (
    <div className={styles.tableSurface}>
      {/* FD-513 — the saved-mappings read drains its pages, but the drain has a
          cap, and a cap that hides what it dropped would be the very defect
          this task fixed. If it bites, say so where the rows would have been.
          Reaching it means ~5,000 saved mappings on one campaign, which is the
          point at which this surface needs a decision rather than a scroll. */}
      {mappingsQuery.data?.truncated && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Not every saved mapping is shown</MessageBarTitle>
            This campaign has more saved identity mappings than this view reads in one
            pass. Approvals and suggestions are unaffected; mappings with no device behind
            them may be missing from the queue.
          </MessageBarBody>
        </MessageBar>
      )}
      {body}
      {query.hasNextPage && !query.isError && (
        <div className={styles.loadMoreRow}>
          <Button
            appearance="secondary"
            size="small"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load more users'}
          </Button>
        </div>
      )}
      {staged.size > 0 && reviewOpen && <IdentityBulkReviewPanel wb={wb} />}
      {staged.size > 0 && (
        <div className={styles.saveBar}>
          <span className={styles.saveBarCount}>
            <span className="font-mono">{staged.size}</span> staged change
            {staged.size === 1 ? '' : 's'} — nothing is written until you save.
          </span>
          <Button
            appearance="subtle"
            aria-expanded={reviewOpen}
            onClick={() => setReviewOpen((v) => !v)}
          >
            {reviewOpen ? 'Hide review' : `Review ${staged.size}`}
          </Button>
          <Button appearance="subtle" onClick={discardStaged} disabled={isSaving}>
            Discard
          </Button>
          <Button appearance="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving
              ? 'Saving…'
              : `Save ${staged.size} change${staged.size === 1 ? '' : 's'}`}
          </Button>
        </div>
      )}
    </div>
  );
}

interface DestinationCellActions {
  expanded: boolean;
  onToggleAlternates: () => void;
  onUseCandidate: (row: IdentitySuggestionUser, candidate: SuggestionCandidate) => void;
}

/**
 * The destination user, named the way the source user is named beside it
 * (2026-09-27 owner decision): display name over the UPN.
 *
 * The UPN is carried as a second line rather than folded away — when the source
 * and destination display names are identical, which is the ordinary case for a
 * real person migrating between tenants, the name alone does not tell an
 * operator which directory this row points at.
 */
function renderDestinationUserCell(
  view: DestinationView,
  styles: QueueStyles,
  actions: DestinationCellActions,
): JSX.Element {
  if (view.kind === 'staged-removal') {
    return <span className="text-zinc-500 line-through">removal staged</span>;
  }
  if (view.kind === 'needs-attention') {
    return <span className="text-zinc-600">{view.message}</span>;
  }
  if (view.kind === 'none') {
    return <span className="text-zinc-400">— no match found</span>;
  }

  const primary = view.displayName ?? view.upn ?? '—';
  const secondary = view.displayName ? view.upn : null;

  const identity = (
    <div className={styles.cellStack}>
      <span className="font-medium text-zinc-900">{primary}</span>
      {secondary && <span className="text-xs text-zinc-500">{secondary}</span>}
    </div>
  );

  if (view.kind === 'mapped') {
    return (
      <span className={styles.destCell}>
        {identity}
        {view.notReady && (
          <Badge appearance="tint" color="warning">
            Destination not ready
          </Badge>
        )}
      </span>
    );
  }

  if (view.kind === 'staged-set') return <span className={styles.destCell}>{identity}</span>;

  const { alternates, row } = view;
  return (
    <div>
      <span className={styles.destCell}>
        {identity}
        {/* FD-511 — the one exception the table did not already state. The
            server returns `accountEnabled` on every candidate and uses it as
            one of the four `autoAcceptEligible` conjuncts, then surfaces
            nothing: a disabled destination silently suppressed the sweep and
            an explicit approve of it succeeded with no warning anywhere. */}
        {view.disabled && (
          <Badge appearance="tint" color="warning">
            Account disabled
          </Badge>
        )}
        {alternates.length > 0 && (
          // The chooser is a control on a clickable row, so it stops
          // propagation: without this, picking an alternate would also open the
          // drawer over the choice being made.
          <span onClick={(e) => e.stopPropagation()}>
            <Button
              size="small"
              appearance="transparent"
              onClick={actions.onToggleAlternates}
              aria-expanded={actions.expanded}
            >
              {actions.expanded
                ? 'Hide other matches'
                : `${alternates.length} other match${alternates.length === 1 ? '' : 'es'}`}
            </Button>
          </span>
        )}
      </span>
      {actions.expanded && alternates.length > 0 && (
        <div className={styles.altList} onClick={(e) => e.stopPropagation()}>
          {alternates.map((alt) => (
            <div key={alt.user.id} className={styles.altRow}>
              {/* Name AND UPN, for the reason the pair of columns above exists
                  (owner, 2026-09-27): on T2T two destination users can carry the
                  SAME display name and be different people. This list is where
                  that bites hardest — the candidates a tier returns are ranked by
                  name-shaped signals, so alternates are MORE likely to share a
                  display name than two arbitrary users, and this is the moment an
                  operator commits to one of them. A chooser that shows only the
                  name cannot be chosen from. */}
              <span className={styles.cellStack}>
                <span className="text-sm text-zinc-700">
                  {alt.user.displayName ?? alt.user.userPrincipalName ?? alt.user.id}
                </span>
                {alt.user.displayName && alt.user.userPrincipalName && (
                  <span className="text-xs text-zinc-500">{alt.user.userPrincipalName}</span>
                )}
              </span>
              <Badge appearance="outline" color="informative">
                {MATCHED_ON_LABELS[alt.matchedOn] ?? alt.matchedOn}
              </Badge>
              <Button
                size="small"
                appearance="subtle"
                onClick={() => actions.onUseCandidate(row, alt)}
              >
                Use this
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Match type — the ranking tier that produced the destination beside it, and
 * nothing else (2026-09-27 owner decision).
 *
 * An em-dash is the honest answer for every row whose destination was not
 * produced by a match: a mapping already written to the server carries no tier
 * (row existence is approval, FD-ADR-037 §3 — there is no stored `matchedOn`),
 * and a manual add or a picker Change is an operator's choice rather than a
 * match. Borrowing `suggestions[0].matchedOn` for those rows would label the
 * destination with the tier of a candidate the operator did not take.
 */
function renderMatchTypeCell(view: DestinationView): JSX.Element {
  const matchedOn =
    view.kind === 'candidate'
      ? view.matchedOn
      : view.kind === 'staged-set'
        ? view.matchedOn
        : undefined;
  if (!matchedOn) return <span className="text-zinc-400">—</span>;
  return (
    <Badge appearance="outline" color="informative">
      {MATCHED_ON_LABELS[matchedOn] ?? matchedOn}
    </Badge>
  );
}
